import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Gift, ChevronRight, Heart, Users, Briefcase, Sparkles, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCurrency } from '@/context/CurrencyContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Elegant icon components for recipients
const RecipientIcon = ({ type, isSelected }) => {
  const baseClass = `w-8 h-8 transition-colors ${isSelected ? 'text-primary' : 'text-muted-foreground group-hover:text-primary'}`;
  
  switch(type) {
    case 'Mother':
      return (
        <svg className={baseClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      );
    case 'Partner':
      return (
        <svg className={baseClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      );
    case 'Friend':
      return (
        <svg className={baseClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="9" cy="7" r="4" />
          <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
          <circle cx="17" cy="7" r="3" />
          <path d="M21 21v-2a3 3 0 0 0-2-2.83" />
        </svg>
      );
    case 'Colleague':
      return (
        <svg className={baseClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
      );
    case 'Self':
      return (
        <svg className={baseClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2z" />
        </svg>
      );
    default:
      return <Sparkles className={baseClass} />;
  }
};

const RECIPIENTS = [
  { id: 'Mother', label: 'Mother', subtitle: 'For the one who nurtures' },
  { id: 'Partner', label: 'Partner', subtitle: 'For your beloved' },
  { id: 'Friend', label: 'Friend', subtitle: 'For cherished bonds' },
  { id: 'Colleague', label: 'Colleague', subtitle: 'For professional warmth' },
  { id: 'Self', label: 'Myself', subtitle: 'You deserve this' },
];

// Smart occasion mapping based on recipient
const OCCASIONS_BY_RECIPIENT = {
  Mother: [
    { id: 'Birthday', label: 'Birthday', subtitle: 'Celebrate her special day' },
    { id: 'Anniversary', label: 'Anniversary', subtitle: 'Honor her journey' },
    { id: "Mother's Day", label: "Mother's Day", subtitle: 'Show your gratitude' },
    { id: 'Festive', label: 'Festive Season', subtitle: 'Share the joy' },
    { id: 'Just Because', label: 'Just Because', subtitle: 'She deserves it' },
  ],
  Partner: [
    { id: 'Birthday', label: 'Birthday', subtitle: 'Make it memorable' },
    { id: 'Anniversary', label: 'Anniversary', subtitle: 'Celebrate your love' },
    { id: "Valentine's Day", label: "Valentine's Day", subtitle: 'Express your heart' },
    { id: 'Festive', label: 'Festive Season', subtitle: 'Create memories together' },
    { id: 'Just Because', label: 'Just Because', subtitle: 'Love needs no reason' },
  ],
  Friend: [
    { id: 'Birthday', label: 'Birthday', subtitle: 'Celebrate your bond' },
    { id: 'Festive', label: 'Festive Season', subtitle: 'Share the festivities' },
    { id: 'Housewarming', label: 'Housewarming', subtitle: 'Warm their new space' },
    { id: 'Thank You', label: 'Thank You', subtitle: 'Show appreciation' },
    { id: 'Just Because', label: 'Just Because', subtitle: 'Friendship celebrated' },
  ],
  Colleague: [
    { id: 'Corporate', label: 'Corporate Gift', subtitle: 'Professional appreciation' },
    { id: 'Farewell', label: 'Farewell', subtitle: 'A memorable goodbye' },
    { id: 'Promotion', label: 'Promotion', subtitle: 'Celebrate their success' },
    { id: 'Festive', label: 'Festive Season', subtitle: 'Season greetings' },
    { id: 'Thank You', label: 'Thank You', subtitle: 'Professional gratitude' },
  ],
  Self: [
    { id: 'Self Care', label: 'Self Care', subtitle: 'Treat yourself' },
    { id: 'Birthday', label: 'My Birthday', subtitle: 'Gift yourself' },
    { id: 'Achievement', label: 'Achievement', subtitle: 'Reward your success' },
    { id: 'Festive', label: 'Festive Season', subtitle: 'New wardrobe addition' },
    { id: 'Just Because', label: 'Just Because', subtitle: 'You deserve it' },
  ],
};

const BUDGETS = [
  { id: '0-3000', label: 'Under ₹3,000', min: 0, max: 3000 },
  { id: '3000-5000', label: '₹3,000 - ₹5,000', min: 3000, max: 5000 },
  { id: '5000-10000', label: '₹5,000 - ₹10,000', min: 5000, max: 10000 },
  { id: '10000+', label: '₹10,000 & Above', min: 10000, max: 100000 },
];

const STYLES = [
  { id: 'Minimal', label: 'Minimal', desc: 'Clean, understated elegance' },
  { id: 'Vibrant', label: 'Vibrant', desc: 'Bold colors and patterns' },
  { id: 'Traditional', label: 'Traditional', desc: 'Heritage-inspired designs' },
  { id: 'Luxe', label: 'Luxe', desc: 'Premium, exclusive pieces' },
];

const GiftingWizard = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(1);
  const [selections, setSelections] = useState({
    recipient: '',
    occasion: '',
    budget: null,
    style: ''
  });
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const { formatPrice, currency } = useCurrency();

  const handleSelect = (field, value) => {
    setSelections(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = async () => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      setLoading(true);
      try {
        const budget = BUDGETS.find(b => b.id === selections.budget);
        const response = await axios.post(`${API}/gifting/recommend`, {
          recipient: selections.recipient,
          occasion: selections.occasion,
          budget_min: budget?.min || 0,
          budget_max: budget?.max || 100000,
          style: selections.style
        }, { params: { currency } });
        setRecommendations(response.data.recommendations || []);
        setStep(5);
      } catch (error) {
        console.error('Error fetching recommendations:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const canProceed = () => {
    if (step === 1) return selections.recipient;
    if (step === 2) return selections.occasion;
    if (step === 3) return selections.budget;
    if (step === 4) return selections.style;
    return true;
  };

  const reset = () => {
    setStep(1);
    setSelections({ recipient: '', occasion: '', budget: null, style: '' });
    setRecommendations([]);
  };

  const handleClose = (isOpen) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl rounded-none border-border p-0 overflow-hidden" data-testid="gifting-wizard">
        {/* Progress bar */}
        <div className="h-1 bg-secondary">
          <div 
            className="h-full bg-primary transition-all duration-500"
            style={{ width: `${(step / 5) * 100}%` }}
          />
        </div>

        <div className="p-8 md:p-10">
          {/* Header */}
          <DialogHeader className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 border border-primary/30 flex items-center justify-center">
                <Gift className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="caption text-primary">Guided Gifting</span>
                {step < 5 && (
                  <p className="text-xs text-muted-foreground">Step {step} of 4</p>
                )}
              </div>
            </div>
            <DialogTitle className="text-2xl md:text-3xl font-light tracking-wide">
              {step === 1 && 'Who are you gifting?'}
              {step === 2 && 'What\'s the occasion?'}
              {step === 3 && 'Your budget range'}
              {step === 4 && 'Their style preference'}
              {step === 5 && 'Curated for You'}
            </DialogTitle>
          </DialogHeader>

          {/* Step 1: Recipient */}
          {step === 1 && (
            <div className="space-y-4" data-testid="step-recipient">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {RECIPIENTS.map(r => (
                  <button
                    key={r.id}
                    onClick={() => handleSelect('recipient', r.id)}
                    className={`group p-6 border text-left transition-all duration-300 flex items-center gap-4 ${
                      selections.recipient === r.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50 hover:bg-secondary/30'
                    }`}
                  >
                    <div className={`w-14 h-14 flex items-center justify-center border transition-colors ${
                      selections.recipient === r.id 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border group-hover:border-primary/30'
                    }`}>
                      <RecipientIcon type={r.id} isSelected={selections.recipient === r.id} />
                    </div>
                    <div>
                      <span className="block font-medium tracking-wide">{r.label}</span>
                      <span className="text-xs text-muted-foreground">{r.subtitle}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Occasion */}
          {step === 2 && (
            <div className="space-y-4" data-testid="step-occasion">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(OCCASIONS_BY_RECIPIENT[selections.recipient] || []).map(o => (
                  <button
                    key={o.id}
                    onClick={() => handleSelect('occasion', o.id)}
                    className={`group p-5 border text-left transition-all duration-300 ${
                      selections.occasion === o.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50 hover:bg-secondary/30'
                    }`}
                  >
                    <span className="block font-medium tracking-wide mb-1">{o.label}</span>
                    <span className="text-xs text-muted-foreground">{o.subtitle}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Budget */}
          {step === 3 && (
            <div className="space-y-4" data-testid="step-budget">
              <div className="grid grid-cols-2 gap-4">
                {BUDGETS.map(b => (
                  <button
                    key={b.id}
                    onClick={() => handleSelect('budget', b.id)}
                    className={`group p-5 border text-center transition-all duration-300 ${
                      selections.budget === b.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50 hover:bg-secondary/30'
                    }`}
                  >
                    <span className="block font-medium tracking-wide">{b.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Style */}
          {step === 4 && (
            <div className="space-y-4" data-testid="step-style">
              <div className="grid grid-cols-2 gap-4">
                {STYLES.map(s => (
                  <button
                    key={s.id}
                    onClick={() => handleSelect('style', s.id)}
                    className={`group p-5 border text-left transition-all duration-300 ${
                      selections.style === s.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50 hover:bg-secondary/30'
                    }`}
                  >
                    <span className="block font-medium tracking-wide mb-1">{s.label}</span>
                    <span className="text-xs text-muted-foreground">{s.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Recommendations */}
          {step === 5 && (
            <div className="space-y-6" data-testid="step-recommendations">
              {recommendations.length > 0 ? (
                <>
                  <p className="text-muted-foreground text-sm">
                    Hand-picked for {selections.recipient.toLowerCase() === 'self' ? 'you' : `your ${selections.recipient.toLowerCase()}`}
                  </p>
                  <div className="grid grid-cols-2 gap-5 max-h-[400px] overflow-y-auto pr-2">
                    {recommendations.map(product => (
                      <Link
                        key={product.id}
                        to={`/product/${product.slug}`}
                        onClick={() => handleClose(false)}
                        className="group"
                      >
                        <div className="aspect-square overflow-hidden mb-3 bg-secondary/30">
                          <img 
                            src={product.images?.[0] || '/images/products/IMG_6195.jpg'}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <h4 className="text-sm font-medium line-clamp-1 tracking-wide">{product.name}</h4>
                        <p className="text-sm text-primary mt-1">{formatPrice(product.display_price)}</p>
                        {product.why_this_fits && (
                          <p className="text-xs text-muted-foreground mt-2 line-clamp-2 italic">"{product.why_this_fits}"</p>
                        )}
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 border border-border mx-auto mb-4 flex items-center justify-center">
                    <Gift className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground mb-6">No products match your criteria right now.</p>
                  <Button onClick={reset} variant="outline" className="rounded-none">
                    Try Different Selections
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between items-center mt-10 pt-6 border-t border-border">
            {step > 1 && step < 5 ? (
              <button 
                onClick={() => setStep(step - 1)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors uppercase tracking-widest"
              >
                Back
              </button>
            ) : step === 5 ? (
              <button 
                onClick={reset}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors uppercase tracking-widest"
              >
                Start Over
              </button>
            ) : (
              <div />
            )}
            
            {step < 5 && (
              <Button 
                className="btn-chaleur-primary min-w-[180px]"
                onClick={nextStep}
                disabled={!canProceed() || loading}
              >
                {loading ? 'Finding...' : step === 4 ? 'See Recommendations' : 'Continue'}
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            )}

            {step === 5 && recommendations.length > 0 && (
              <Button asChild className="btn-chaleur-primary">
                <Link to="/shop" onClick={() => handleClose(false)}>
                  Explore All
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GiftingWizard;
